import React, { useState } from 'react';
import { Search, MapPin, Truck, CheckCircle, XCircle, Package, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';

import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { Base_Url, getpincode } from '../../../../config/config';
import { toast } from 'react-toastify';

const PincodeService = () => {
  const [pincode, setPincode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [pincodeData, setPincodeData] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleCheck = async () => {
    setIsChecking(true);
    setShowResults(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data && data.status === "SUCCESS") {
        setPincodeData(data.data);
        setIsChecking(false);
        setShowResults(true);
      } else if (data && data.message === "Unauthorized access - Invalid token") {
        toast.error(data.message + " Please login Again");
        setIsChecking(false);
      } else if (data && data.status === "FAIL") {
        toast.error("Something Went Wrong Please Try Again With Another Pincode");
        setIsChecking(false);
      } else {
        toast.error("Failed to fetch city and state data");
        setIsChecking(false);
      }
    } catch (error) {
      console.error('Error fetching pincode data:', error);
      toast.error("Error fetching pincode data");
      setIsChecking(false);
    }
  };

  const hasResults = showResults && pincodeData && pincodeData.length > 0;
  const noResults = showResults && (!pincodeData || pincodeData.length === 0);

  // Sample pincodes for quick test
  const samplePincodes = ['110001', '400001', '560001', '700001'];

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-gray-50'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-white dark:bg-black dark:text-white md:bg-transparent  md:w-64 md:z-20 border-r border-gray-200 dark:border-gray-800`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}

        <div className='flex-1 dark:bg-black p-4 md:p-6 w-full bg-gray-200 overflow-hidden flex flex-col'>

          {/* Header */}
          <div className="flex-shrink-0 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <Package size={16} className="text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-800 dark:text-white tracking-tight">Pincode Service Checker</h1>
                <p className="text-[10px] text-black dark:text-gray-500">Check delivery availability in your area instantly</p>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 flex-1 min-h-0">

            {/* Left - Search Card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col shrink-0">
              <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <MapPin size={13} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <h2 className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Check Pincode</h2>
                    <p className="text-[8px] text-gray-400 dark:text-gray-500">Enter 6-digit pincode</p>
                  </div>
                </div>
              </div>

              <div className="p-4 flex flex-col gap-3.5">
                <div>
                  <label htmlFor="pincode" className="block text-[9px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Pincode
                  </label>
                  <div className="relative">
                    <input
                      id="pincode"
                      type="text"
                      maxLength="6"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="Enter 6-digit pincode"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-4 pr-3 py-2 text-[11px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent transition-all"
                    />
                    {pincode.length === 6 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-medium text-green-500">✓</span>
                    )}
                  </div>
                  {pincode.length > 0 && pincode.length < 6 && (
                    <p className="text-[8px] text-gray-400 dark:text-gray-500 mt-1">Enter 6 digits</p>
                  )}
                </div>

                <button
                  onClick={handleCheck}
                  disabled={isChecking || pincode.length !== 6}
                  className={`w-full py-2 text-[10px] font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isChecking || pincode.length !== 6
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-gray-800 dark:bg-white text-white dark:text-gray-800 hover:opacity-85 shadow-sm'
                  }`}
                >
                  {isChecking ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Search size={13} />
                      Check Availability
                    </>
                  )}
                </button>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[8px] text-gray-400 dark:text-gray-500">Quick test:</span>
                  {samplePincodes.map((code) => (
                    <button
                      key={code}
                      onClick={() => setPincode(code)}
                      className="px-2 py-0.5 text-[8px] font-medium border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400"
                    >
                      {code}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="flex items-center gap-2 p-2 border border-gray-100 dark:border-gray-800 rounded-lg">
                    <Truck size={13} className="text-gray-500 dark:text-gray-400" />
                    <span className="text-[8px] font-medium text-gray-600 dark:text-gray-400">Multiple Couriers</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 border border-gray-100 dark:border-gray-800 rounded-lg">
                    <Sparkles size={13} className="text-gray-500 dark:text-gray-400" />
                    <span className="text-[8px] font-medium text-gray-600 dark:text-gray-400">Instant Check</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Results Panel */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">

              {/* Results Header */}
              <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${hasResults ? 'bg-green-500' : noResults ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                  <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                    {hasResults ? `${pincodeData.length} result${pincodeData.length > 1 ? 's' : ''} found` : noResults ? 'No results' : 'Ready to check'}
                  </span>
                </div>
                {hasResults && (
                  <span className="text-[8px] text-gray-400 dark:text-gray-500">Pincode: {pincode}</span>
                )}
              </div>

              {/* Results Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-1">
                {hasResults && (
                  <div className="space-y-1">
                    {pincodeData.map((shipment, index) => {
                      const partners = [];
                      if (shipment.delhivery === "1") partners.push({ name: "Delhivery", icon: "🚚" });
                      if (shipment.shiprocket === "1") partners.push({ name: "Shiprocket", icon: "📦" });
                      if (shipment.ecom === "1") partners.push({ name: "Ecom", icon: "🏪" });
                      if (shipment.ekart === "1") partners.push({ name: "Ekart", icon: "🛵" });
                      if (shipment.xpressbees === "1") partners.push({ name: "XpressBees", icon: "🐝" });

                      return (
                        <div key={index} className="p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <div className="text-sm font-bold text-gray-800 dark:text-white font-mono">{shipment.pincode}</div>
                                <div className="text-[8px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">Pincode</div>
                              </div>
                              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700"></div>
                              <div>
                                <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">{shipment.city}</div>
                                <div className="text-[9px] text-gray-400 dark:text-gray-500">{shipment.state}</div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {partners.length > 0 ? (
                                partners.map((partner, idx) => (
                                  <span key={idx} className="px-2 py-1 rounded-md text-[8px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center gap-0.5">
                                    {partner.icon} {partner.name}
                                  </span>
                                ))
                              ) : (
                                <span className="px-2 py-1 rounded-md text-[8px] font-medium bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 flex items-center gap-1">
                                  <XCircle size={11} /> No Partner
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {noResults && (
                  <div className="flex flex-col items-center justify-center h-full text-center p-6">
                    <div className="w-16 h-16 rounded-full border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 flex items-center justify-center mb-3">
                      <AlertCircle size={28} className="text-red-500 dark:text-red-400" />
                    </div>
                    <h3 className="text-sm font-bold text-red-600 dark:text-red-400">Service Unavailable</h3>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 max-w-sm mt-1">
                      We don't have delivery partners for pincode <span className="font-bold text-gray-700 dark:text-gray-300">{pincode}</span> yet.
                    </p>
                    <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">Please try another pincode or check back later.</p>
                    <button
                      onClick={() => { setPincode(''); setShowResults(false); }}
                      className="mt-3 px-4 py-1.5 text-[9px] font-medium border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!showResults && (
                  <div className="flex flex-col items-center justify-center h-full text-center p-6">
                    <div className="w-16 h-16 rounded-full border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 flex items-center justify-center mb-3">
                      <MapPin size={28} className="text-gray-300 dark:text-gray-600" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-400 dark:text-gray-500">Enter a Pincode</h3>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Enter a 6-digit pincode on the left panel</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                      <span className="text-[8px] text-gray-400 dark:text-gray-500">Check availability instantly</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PincodeService;